import React, { useState, useEffect, useRef } from "react";
import { Button, Col, Collapse, Container, Form, Row, Table, Card, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "state/hooks";
import { getUserState } from "state/slices/user-slice";
import { getGroupState } from "state/slices/group-slice";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text";
import BlockyMediumText from "components/custom-texts/blocky-medium-text";
import store from "state/store";
import { useAuth } from "hooks/auth";
import { ConsoleMessage } from "hooks/useConsoleCapture";
import { getConsoleMessages, clearConsoleMessages } from "state/slices/console-slice";
import { getNetworkRequests, clearNetworkRequests } from "state/slices/network-slice";

const getAllCookies = (): Record<string, string> => {
  const cookies = document.cookie.split("; ");
  const cookieObj: Record<string, string> = {};
  cookies.forEach((cookie) => {
    const [key, value] = cookie.split("=");
    if (key && value) {
      cookieObj[key] = decodeURIComponent(value);
    }
  });
  return cookieObj;
};

const getEnvironmentInfo = () => {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    cookieEnabled: navigator.cookieEnabled,
    onLine: navigator.onLine,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    windowSize: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timestamp: new Date().toISOString(),
  };
};

const getSessionStorageData = () => {
  const data: Record<string, string | null> = {};
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key) {
      data[key] = sessionStorage.getItem(key);
    }
  }
  return data;
};

const DebugPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = useAppSelector(getUserState);
  const group = useAppSelector(getGroupState);
  const dispatch = useAppDispatch();

  const [localStorageKey, setLocalStorageKey] = useState("");
  const [localStorageValue, setLocalStorageValue] = useState("");
  const [localStorageData, setLocalStorageData] = useState(
    Object.keys(localStorage).map((key) => ({
      key,
      value: localStorage.getItem(key),
    }))
  );

  const [sessionStorageData, setSessionStorageData] = useState(getSessionStorageData());
  const [reduxStateOpen, setReduxStateOpen] = useState(false);
  const [environmentOpen, setEnvironmentOpen] = useState(false);
  const [sessionStorageOpen, setSessionStorageOpen] = useState(false);
  const [consoleLogsOpen, setConsoleLogsOpen] = useState(false);
  const [networkRequestsOpen, setNetworkRequestsOpen] = useState(false);
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
  const [autoScrollLogs, setAutoScrollLogs] = useState(true);
  const logsEndRef = useRef<HTMLDivElement>(null);
  
  const consoleLogs = useAppSelector(getConsoleMessages);
  const networkRequests = useAppSelector(getNetworkRequests);
  
  const handleClearConsoleLogs = () => {
    dispatch(clearConsoleMessages());
  };

  const handleClearNetworkRequests = () => {
    dispatch(clearNetworkRequests());
  };

  const reduxState = store.getState();
  const environmentInfo = getEnvironmentInfo();
  const cookieData = getAllCookies();
  const commitHash = process.env.REACT_APP_COMMIT_HASH || "Not available";
  const nodeEnv = process.env.NODE_ENV || "Not available";
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "Not available";

  const handleSetLocalStorage = () => {
    if (localStorageKey && localStorageValue) {
      localStorage.setItem(localStorageKey, localStorageValue);
      updateLocalStorageData();
      setLocalStorageKey("");
      setLocalStorageValue("");
    }
  };

  const handleRemoveLocalStorage = (key: string) => {
    localStorage.removeItem(key);
    updateLocalStorageData();
  };

  const updateLocalStorageData = () => {
    setLocalStorageData(
      Object.keys(localStorage).map((key) => ({
        key,
        value: localStorage.getItem(key),
      }))
    );
  };

  const handleClearAllStorage = () => {
    if (window.confirm("Are you sure you want to clear all localStorage and sessionStorage?")) {
      localStorage.clear();
      sessionStorage.clear();
      updateLocalStorageData();
      setSessionStorageData({});
    }
  };

  const getLogLevelColor = (type: ConsoleMessage['type']): string => {
    switch (type) {
      case 'error': return '#dc3545';
      case 'warn': return '#ffc107';
      case 'info': return '#17a2b8';
      default: return '#212529';
    }
  };

  const getLogLevelBadge = (type: ConsoleMessage['type']): string => {
    switch (type) {
      case 'error': return 'danger';
      case 'warn': return 'warning';
      case 'info': return 'info';
      default: return 'primary';
    }
  };

  const handleExportDebugInfo = () => {
    const debugInfo = {
      timestamp: new Date().toISOString(),
      commitHash,
      nodeEnv,
      backendUrl,
      currentUrl: window.location.href,
      user: {
        id: user.id,
        super_admin: user.super_admin,
        authSignedIn: auth.signedIn,
      },
      group: {
        id: group.id,
        name: group.name,
      },
      environment: environmentInfo,
      localStorage: Object.fromEntries(localStorageData.map(item => [item.key, item.value])),
      sessionStorage: sessionStorageData,
      cookies: cookieData,
      consoleLogs: consoleLogs.map(log => ({
        timestamp: log.timestamp,
        type: log.type,
        message: log.message,
        stack: log.stack,
      })),
      networkRequests: networkRequests.map(request => ({
        timestamp: request.timestamp,
        url: request.url,
        method: request.method,
        status: request.status,
        duration: request.duration,
        error: request.error,
        responseBody: request.responseBody,
        requestHeaders: request.requestHeaders,
      })),
      reduxState,
    };

    const blob = new Blob([JSON.stringify(debugInfo, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sparket-debug-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setSessionStorageData(getSessionStorageData());
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (autoScrollLogs && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [consoleLogs, autoScrollLogs]);

  return (
    <Container
      fluid
      className="p-4"
      style={{
        backgroundColor: "white",
        maxWidth: "100vw",
        overflowX: "hidden",
        margin: "0 auto",
        width: "100%",
        minHeight: "100vh",
      }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="mt-4 mb-4">🔧 Debug Page</h1>
        <div>
          <Button variant="success" onClick={handleExportDebugInfo} className="me-2">
            Export Debug Info
          </Button>
          <Button variant="secondary" onClick={() => navigate("/")}>
            Go Home
          </Button>
        </div>
      </div>

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <h4>🏗️ Build Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <BlockyMediumText style={{ color: "#666" }}>Commit Hash:</BlockyMediumText>
                  <BlockyHeavyText style={{ fontFamily: "monospace", color: "#007bff" }}>
                    {commitHash}
                  </BlockyHeavyText>
                </Col>
                <Col md={4}>
                  <BlockyMediumText style={{ color: "#666" }}>Environment:</BlockyMediumText>
                  <Badge bg={nodeEnv === "production" ? "success" : "warning"}>
                    {nodeEnv}
                  </Badge>
                </Col>
                <Col md={4}>
                  <BlockyMediumText style={{ color: "#666" }}>Backend URL:</BlockyMediumText>
                  <BlockyHeavyText style={{ fontSize: "12px", wordBreak: "break-all" }}>
                    {backendUrl}
                  </BlockyHeavyText>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header>
              <h4>👤 User Information</h4>
            </Card.Header>
            <Card.Body>
              <BlockyMediumText style={{ color: "#666" }}>User ID:</BlockyMediumText>
              <BlockyHeavyText style={{ fontFamily: "monospace" }}>
                {user.id || "Not logged in"}
              </BlockyHeavyText>
              <div className="mt-2">
                <Badge bg={user.super_admin ? "danger" : "secondary"}>
                  {user.super_admin ? "Super Admin" : "Regular User"}
                </Badge>
                <Badge bg={auth.signedIn ? "success" : "secondary"} className="ms-2">
                  {auth.signedIn ? "Signed In" : "Not Signed In"}
                </Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card>
            <Card.Header>
              <h4>🏢 Group Information</h4>
            </Card.Header>
            <Card.Body>
              <BlockyMediumText style={{ color: "#666" }}>Group ID:</BlockyMediumText>
              <BlockyHeavyText style={{ fontFamily: "monospace" }}>
                {group.id || "No group loaded"}
              </BlockyHeavyText>
              {group.name && (
                <div className="mt-2">
                  <BlockyMediumText style={{ color: "#666" }}>Group Name:</BlockyMediumText>
                  <BlockyHeavyText>{group.name}</BlockyHeavyText>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header 
              onClick={() => setEnvironmentOpen(!environmentOpen)} 
              style={{ cursor: "pointer" }}
              className="d-flex justify-content-between align-items-center"
            >
              <h4>🌍 Environment Info</h4>
              <Badge bg="info">{environmentOpen ? "Click to Collapse" : "Click to Expand"}</Badge>
            </Card.Header>
            <Collapse in={environmentOpen}>
              <Card.Body>
                <Table striped bordered hover responsive size="sm">
                  <tbody>
                    {Object.entries(environmentInfo).map(([key, value]) => (
                      <tr key={key}>
                        <td style={{ fontWeight: "bold", width: "200px" }}>{key}</td>
                        <td style={{ fontFamily: "monospace", fontSize: "12px" }}>{value}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Collapse>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header 
              onClick={() => setReduxStateOpen(!reduxStateOpen)} 
              style={{ cursor: "pointer" }}
              className="d-flex justify-content-between align-items-center"
            >
              <h4>🔄 Redux State</h4>
              <Badge bg="warning">{reduxStateOpen ? "Click to Collapse" : "Click to Expand"}</Badge>
            </Card.Header>
            <Collapse in={reduxStateOpen}>
              <Card.Body>
                <div style={{
                  padding: "15px",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  backgroundColor: "#f8f9fa",
                  maxHeight: "500px",
                  overflowY: "scroll",
                  fontFamily: "monospace",
                  fontSize: "12px",
                  whiteSpace: "pre-wrap"
                }}>
                  {JSON.stringify(reduxState, null, 2)}
                </div>
              </Card.Body>
            </Collapse>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header 
              onClick={() => setConsoleLogsOpen(!consoleLogsOpen)} 
              style={{ cursor: "pointer" }}
              className="d-flex justify-content-between align-items-center"
            >
              <h4>📝 Console Logs</h4>
              <div className="d-flex align-items-center gap-2">
                <Badge bg="primary">{consoleLogs.length} logs</Badge>
                <Badge bg="info">{consoleLogsOpen ? "Click to Collapse" : "Click to Expand"}</Badge>
              </div>
            </Card.Header>
            <Collapse in={consoleLogsOpen}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <Button variant="danger" size="sm" onClick={handleClearConsoleLogs}>
                      Clear Logs
                    </Button>
                    <Form.Check 
                      type="switch"
                      id="auto-scroll-switch"
                      label="Auto-scroll"
                      checked={autoScrollLogs}
                      onChange={(e) => setAutoScrollLogs(e.target.checked)}
                    />
                  </div>
                  <BlockyMediumText style={{ color: "#666", fontSize: "12px" }}>
                    Showing logs from when debug page opened
                  </BlockyMediumText>
                </div>
                
                <div style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  backgroundColor: "#000",
                  maxHeight: "400px",
                  overflowY: "scroll",
                  fontFamily: "monospace",
                  fontSize: "13px",
                  padding: "10px"
                }}>
                  {consoleLogs.length === 0 ? (
                    <div style={{ color: "#6c757d", textAlign: "center", padding: "20px" }}>
                      No console logs yet. Try interacting with the app or run console.log() in browser console.
                    </div>
                  ) : (
                    consoleLogs.map((log) => (
                      <div 
                        key={log.id} 
                        style={{ 
                          marginBottom: "8px", 
                          padding: "6px 8px",
                          backgroundColor: "rgba(255,255,255,0.05)",
                          borderRadius: "4px",
                          borderLeft: `4px solid ${getLogLevelColor(log.type)}`
                        }}
                      >
                        <div style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          gap: "8px", 
                          marginBottom: "4px" 
                        }}>
                          <Badge bg={getLogLevelBadge(log.type)} style={{ fontSize: "10px" }}>
                            {log.type.toUpperCase()}
                          </Badge>
                          <span style={{ 
                            color: "#6c757d", 
                            fontSize: "11px" 
                          }}>
                            {new Date(log.timestamp).toLocaleTimeString()}.{new Date(log.timestamp).getMilliseconds().toString().padStart(3, '0')}
                          </span>
                        </div>
                        <div style={{ 
                          color: getLogLevelColor(log.type),
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word"
                        }}>
                          {log.message}
                        </div>
                        {log.stack && (
                          <div style={{ 
                            color: "#6c757d",
                            fontSize: "11px",
                            whiteSpace: "pre-wrap",
                            marginTop: "4px",
                            paddingTop: "4px",
                            borderTop: "1px solid rgba(255,255,255,0.1)"
                          }}>
                            {log.stack}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  <div ref={logsEndRef} />
                </div>
              </Card.Body>
            </Collapse>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header 
              onClick={() => setNetworkRequestsOpen(!networkRequestsOpen)} 
              style={{ cursor: "pointer" }}
              className="d-flex justify-content-between align-items-center"
            >
              <h4>🌐 Network Requests</h4>
              <div className="d-flex align-items-center gap-2">
                <Badge bg="primary">{networkRequests.length} requests</Badge>
                <Badge bg="info">{networkRequestsOpen ? "Click to Collapse" : "Click to Expand"}</Badge>
              </div>
            </Card.Header>
            <Collapse in={networkRequestsOpen}>
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <Button variant="danger" size="sm" onClick={handleClearNetworkRequests}>
                      Clear Requests
                    </Button>
                  </div>
                  <BlockyMediumText style={{ color: "#666", fontSize: "12px" }}>
                    Showing requests from when debug page opened
                  </BlockyMediumText>
                </div>
                
                <div style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  backgroundColor: "#f8f9fa",
                  maxHeight: "400px",
                  overflowY: "scroll",
                  padding: "10px"
                }}>
                  {networkRequests.length === 0 ? (
                    <div style={{ color: "#6c757d", textAlign: "center", padding: "20px" }}>
                      No network requests yet. Try navigating or refreshing the page.
                    </div>
                  ) : (
                    networkRequests.map((request) => (
                      <div key={request.id} style={{
                        marginBottom: "12px",
                        padding: "12px",
                        backgroundColor: "white",
                        borderRadius: "6px",
                        border: "1px solid #e0e0e0",
                        cursor: "pointer"
                      }}
                      onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
                      >
                        {/* Header row with main info */}
                        <div style={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          marginBottom: "8px",
                          flexWrap: "wrap",
                          gap: "8px"
                        }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1, minWidth: 0 }}>
                            <Badge 
                              bg={request.method === 'GET' ? 'primary' : request.method === 'POST' ? 'success' : 'secondary'}
                              style={{ fontSize: "10px" }}
                            >
                              {request.method}
                            </Badge>
                            
                            {request.status && (
                              <Badge 
                                bg={request.status < 300 ? 'success' : request.status < 400 ? 'warning' : 'danger'}
                                style={{ fontSize: "10px" }}
                              >
                                {request.status}
                              </Badge>
                            )}
                            {request.error && (
                              <Badge bg="danger" style={{ fontSize: "10px" }}>
                                ERROR
                              </Badge>
                            )}
                            
                            <span style={{ fontSize: "11px", color: "#6c757d" }}>
                              {request.duration ? `${request.duration}ms` : '-'}
                            </span>
                          </div>
                          
                          <span style={{ fontSize: "10px", color: "#6c757d", whiteSpace: "nowrap" }}>
                            {new Date(request.timestamp).toLocaleTimeString()}.{new Date(request.timestamp).getMilliseconds().toString().padStart(3, '0')}
                          </span>
                        </div>
                        
                        {/* URL - break it nicely for mobile */}
                        <div style={{
                          fontFamily: "monospace",
                          fontSize: "11px",
                          color: "#333",
                          wordBreak: "break-all",
                          backgroundColor: "#f8f9fa",
                          padding: "6px 8px",
                          borderRadius: "4px",
                          marginBottom: expandedRequest === request.id ? "12px" : "0"
                        }}>
                          {request.url}
                        </div>
                        
                        {/* Expandable request details */}
                        {expandedRequest === request.id && (
                          <div>
                            {/* Request Headers */}
                            {request.requestHeaders && Object.keys(request.requestHeaders).length > 0 && (
                              <>
                                <h6 style={{ fontSize: "12px", fontWeight: "bold", marginBottom: "8px", color: "#666" }}>
                                  Request Headers:
                                </h6>
                                <div style={{
                                  backgroundColor: "#f8f9fa",
                                  border: "1px solid #dee2e6",
                                  padding: "8px",
                                  borderRadius: "4px",
                                  fontFamily: "monospace",
                                  fontSize: "11px",
                                  marginBottom: "12px"
                                }}>
                                  {Object.entries(request.requestHeaders).map(([key, value]) => (
                                    <div key={key} style={{ marginBottom: "4px" }}>
                                      <span style={{ color: "#007bff", fontWeight: "bold" }}>{key}:</span>{' '}
                                      <span style={{ color: "#333" }}>{value}</span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                            
                            <h6 style={{ fontSize: "12px", fontWeight: "bold", marginBottom: "8px", color: "#666" }}>
                              Response Body:
                            </h6>
                            <div style={{
                              backgroundColor: "#000",
                              color: "#00ff00",
                              padding: "10px",
                              borderRadius: "4px",
                              fontFamily: "monospace",
                              fontSize: "11px",
                              maxHeight: "200px",
                              overflowY: "auto",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word"
                            }}>
                              {request.responseBody || '(No response body captured)'}
                            </div>
                            
                            {/* Debug info */}
                                                         <div style={{ 
                               fontSize: "10px", 
                               color: "#6c757d", 
                               marginTop: "8px",
                               backgroundColor: "#f8f9fa",
                               padding: "6px",
                               borderRadius: "4px"
                             }}>
                               <strong>Debug:</strong> Status: {request.status || 'pending'}, 
                               Duration: {request.duration || 'N/A'}ms, 
                               Headers: {Object.keys(request.requestHeaders || {}).length},
                               Response length: {request.responseBody?.length || 0} chars,
                               Error: {request.error || 'none'}
                             </div>
                          </div>
                        )}
                        
                        {expandedRequest === request.id && (
                          <div style={{ 
                            fontSize: "10px", 
                            color: "#6c757d", 
                            marginTop: "8px",
                            textAlign: "center"
                          }}>
                            Click to collapse
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </Card.Body>
            </Collapse>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <h4>📍 Current Location</h4>
            </Card.Header>
            <Card.Body>
              <div style={{
                padding: "15px",
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                backgroundColor: "#f8f9fa",
                fontFamily: "monospace",
                fontSize: "14px",
                wordBreak: "break-all"
              }}>
                {window.location.href}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4>💾 Local Storage Management</h4>
              <Button variant="danger" size="sm" onClick={handleClearAllStorage}>
                Clear All Storage
              </Button>
            </Card.Header>
            <Card.Body>
              <Form>
                <Row>
                  <Col md={5}>
                    <Form.Group controlId="localStorageKey">
                      <Form.Label>Key</Form.Label>
                      <Form.Control
                        type="text"
                        value={localStorageKey}
                        onChange={(e) => setLocalStorageKey(e.target.value)}
                        placeholder="Enter local storage key"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group controlId="localStorageValue">
                      <Form.Label>Value</Form.Label>
                      <Form.Control
                        type="text"
                        value={localStorageValue}
                        onChange={(e) => setLocalStorageValue(e.target.value)}
                        placeholder="Enter local storage value"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end">
                    <Button variant="primary" onClick={handleSetLocalStorage}>
                      Set
                    </Button>
                  </Col>
                </Row>
              </Form>

              <h5 className="mt-4">Local Storage Data</h5>
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {localStorageData.map((item) => (
                    <tr key={item.key}>
                      <td style={{ fontWeight: "bold", fontFamily: "monospace" }}>{item.key}</td>
                      <td
                        style={{
                          maxWidth: "300px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontFamily: "monospace",
                          fontSize: "12px"
                        }}
                        title={item.value || ""}
                      >
                        {item.value}
                      </td>
                      <td>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveLocalStorage(item.key)}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header 
              onClick={() => setSessionStorageOpen(!sessionStorageOpen)} 
              style={{ cursor: "pointer" }}
              className="d-flex justify-content-between align-items-center"
            >
              <h4>📝 Session Storage</h4>
              <Badge bg="secondary">{sessionStorageOpen ? "Click to Collapse" : "Click to Expand"}</Badge>
            </Card.Header>
            <Collapse in={sessionStorageOpen}>
              <Card.Body>
                <Table striped bordered hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(sessionStorageData).map(([key, value]) => (
                      <tr key={key}>
                        <td style={{ fontWeight: "bold", fontFamily: "monospace" }}>{key}</td>
                        <td
                          style={{
                            maxWidth: "300px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontFamily: "monospace",
                            fontSize: "12px"
                          }}
                          title={value || ""}
                        >
                          {value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Collapse>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <Card.Header>
              <h4>🍪 Cookies</h4>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive size="sm">
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(cookieData).map(([key, value]) => (
                    <tr key={key}>
                      <td style={{ fontWeight: "bold", fontFamily: "monospace" }}>{key}</td>
                      <td
                        style={{
                          maxWidth: "300px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontFamily: "monospace",
                          fontSize: "12px"
                        }}
                        title={value}
                      >
                        {value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default DebugPage; 